/**
 * The "Frequently Asked Questions" block at the bottom of both program pages.
 *
 * TLDP.cshtml and TBIGIN.cshtml carried a byte-identical copy of the last two
 * groups ("About The Applications" and "About The Stages Process"); only the
 * first group differs. They share one component here, with the differing group
 * passed in.
 *
 * The accordion is CSS-only — a hidden checkbox plus a `<label>`, styled by
 * `.accordion-input` / `.accordion-label` / `.p` in landing-page.css. The ids
 * are namespaced per page because both originals used `faq-1..3`, which would
 * now collide if the two pages were ever rendered together.
 */
export interface FaqItem {
  question: string
  answer: string
}

const APPLICATION_FAQS: FaqItem[] = [
  {
    question: 'What should be done if this website encounters an error?',
    answer: 'You can report the issue to recruitment.tbg@tower-bersama.com',
  },
  {
    question: 'Do all fields need to be filled in?',
    answer:
      'Yes, all fields must be filled in. Make sure to pay attention to the text format in each field.',
  },
  {
    question: 'Is there anything that needs to be done after a successful submission?',
    answer:
      'Please wait for further information from us. Ensure that after submission, you receive a confirmation email. If you do not receive it, you can report the issue to our email recruitment.tbg@tower-bersama.com',
  },
]

const STAGES_FAQS: FaqItem[] = [
  {
    question: 'How can I get updates on the recruitment process?',
    answer:
      'Informations related to the recruitment process will be informed via our official WhatsApp account.',
  },
  {
    question: 'Will the stages be held online or offline?',
    answer:
      'All stages will be conducted online, except for the FGD and Panel Presentation. It will be held offline in Jakarta, Bandung, Semarang, & Surabaya. You can choose the nearest city to your domicile.',
  },
  {
    question:
      'What should I do if the website encounters an error or suddenly exits during the psychological test?',
    answer:
      'Ensure you complete the psychological test until you see a note thanking you for completing it. If an error occurs or the website suddenly exits, please refresh & re-open the website by entering the same test code.',
  },
  {
    question: 'Can the psychological test be done on a mobile phone?',
    answer:
      'Yes, but there may be display limitations. We recommend using a laptop or PC for the test.',
  },
]

export function ProgramFaq({
  idPrefix,
  programFaqs,
}: {
  idPrefix: string
  programFaqs: FaqItem[]
}): JSX.Element {
  return (
    <section className="bg-white pt-0">
      <div className="container pt-5">
        <div className="row justify-content-center">
          <div className="col-lg-5 col-md-12">
            <h1
              className="wow animate__fadeInUp section-title text-TBG large-font text-TBG-center"
              data-wow-delay="0.2s"
              style={{ marginTop: '1em' }}
            >
              Frequently Asked Questions
            </h1>
          </div>
          <div className="col-lg-7 col-md-12">
            <div className="row justify-content-center text-center gy-sm-4 gy-3">
              <FaqGroup id={`${idPrefix}-faq-1`} title="About The Program" items={programFaqs} />
              <FaqGroup
                id={`${idPrefix}-faq-2`}
                title="About The Applications"
                items={APPLICATION_FAQS}
              />
              <FaqGroup
                id={`${idPrefix}-faq-3`}
                title="About The Stages Process"
                items={STAGES_FAQS}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function FaqGroup({
  id,
  title,
  items,
}: {
  id: string
  title: string
  items: FaqItem[]
}): JSX.Element {
  return (
    <div className="c">
      {/*
        Uncontrolled on purpose: the checkbox IS the open/closed state, and the
        CSS reads it via :checked. Adding React state would mean re-implementing
        the styling that already works.
      */}
      <input type="checkbox" className="accordion-input" id={id} />
      <h1 className="accordion-tldp-header">
        <label className="accordion-label" htmlFor={id}>
          {title}
        </label>
      </h1>
      <div className="p">
        {items.map((item) => (
          <div key={item.question} className="alert alert-secondary" role="alert">
            <h5>{item.question}</h5>
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0">
                <i className="ti ti-checkbox h2 f-w-400 mb-0" />
              </div>
              <div className="flex-grow-1 ms-3">{item.answer}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
